// Lighting core: the light math shared by the Node engine and the browser
// preview. Pure functions, no I/O, no DOM. See SPEC section 9.1 (roster
// item 5: Lighting Engine, dual role) and 5.2 (lighting as a modifier).
//
// Selectable factors: the caller may override targetFc (brightness) and
// fixtureCount; the plan recomputes lumens, spacing, and per-fixture output
// consistently, and reports the pre-override recommendation alongside. The
// preview exposes these as sliders. CCT override lives in the UI layer
// because it does not change the lumen math, only how color is judged.
//
// The engine RECOMMENDS a light temperature (CCT) and a brightness plan;
// it does not consume a pre-chosen bulb. Color math (color.ts) then reads
// the recommended CCT so a paint is judged under the light it will live in.

import type {
  RoomType,
  Orientation,
  DaylightLevel,
  Objective,
} from "./types.js";

export interface LightingInput {
  roomType: RoomType;
  sqft: number;
  ceilingHeightFt: number;
  orientation: Orientation;
  daylight: DaylightLevel;
  objective: Objective;
  // User-selectable overrides. Omit to use the room's recommendation.
  // Brightness feeds the lumen method; fixture count re-lays-out the grid.
  targetFcOverride?: number;
  fixtureCountOverride?: number;
  // Real room dimensions, if known. When given, the grid and spacing are
  // exact instead of derived from area with an assumed aspect ratio.
  roomLengthFt?: number;
  roomWidthFt?: number;
}

export type LightLayerKind = "ambient" | "task" | "accent" | "feature";

export interface LightLayer {
  kind: LightLayerKind;
  label: string;
  detail: string;
}

export interface LightingPlan {
  cct: number; // recommended correlated color temperature, Kelvin
  cctBand: [number, number]; // acceptable range for the room
  cctLabel: string; // "Warm white" ... "Cool white"
  targetFc: number; // ambient target, foot-candles (daylight-adjusted)
  ambientLumens: number; // lumen-method total for the ambient layer
  spacingFt: number; // recessed-fixture spacing, on center
  wallOffsetFt: number; // offset of the first row from the walls
  cols: number;
  rows: number;
  fixtureCount: number; // recessed cans on the grid (may be user-set)
  lumensPerFixture: number; // ambientLumens / fixtureCount
  recommendedTargetFc: number; // what the room profile suggests, pre-override
  recommendedFixtureCount: number; // spacing-grid count, pre-override
  layers: LightLayer[]; // ambient + room-appropriate task/accent/feature
  reasoning: string[]; // plain-language trace, one line per point
}

// Per-room-type lighting profile. fc = ambient target foot-candles; cct is
// the recommended Kelvin and its acceptable band; cu = coefficient of
// utilization (higher for bright, reflective rooms) used in the lumen
// method. Values track common IES-style residential guidance.
interface RoomLightingProfile {
  fc: number;
  cct: number;
  cctBand: [number, number];
  cu: number;
}

export const ROOM_LIGHTING: Record<RoomType, RoomLightingProfile> = {
  kitchen: { fc: 35, cct: 3500, cctBand: [3000, 4000], cu: 0.62 },
  bath:    { fc: 35, cct: 3000, cctBand: [3000, 3500], cu: 0.60 },
  bedroom: { fc: 15, cct: 2700, cctBand: [2700, 3000], cu: 0.55 },
  living:  { fc: 20, cct: 2700, cctBand: [2700, 3000], cu: 0.55 },
  dining:  { fc: 30, cct: 2700, cctBand: [2700, 3000], cu: 0.55 },
  laundry: { fc: 30, cct: 4000, cctBand: [3500, 4500], cu: 0.62 },
  office:  { fc: 40, cct: 4000, cctBand: [3500, 4500], cu: 0.62 },
  other:   { fc: 20, cct: 3000, cctBand: [2700, 3500], cu: 0.58 },
};

const LLF = 0.8; // light loss factor (LED lumen depreciation + dirt)
const AVG_ROOM_RATIO = 1.4; // assumed length:width for the spacing grid

// Lay out a desired fixture COUNT as a cols x rows grid that roughly matches
// the room's aspect, so a user-chosen count still draws a sensible layout.
function gridForCount(count: number, length: number, width: number): { cols: number; rows: number } {
  const aspect = length / width; // >= 1 for longer-than-wide rooms
  const cols = Math.max(1, Math.round(Math.sqrt(count * aspect)));
  const rows = Math.max(1, Math.round(count / cols));
  return { cols, rows };
}

export function cctLabel(cct: number): string {
  if (cct <= 2700) return "Warm white";
  if (cct < 3300) return "Soft white";
  if (cct < 4000) return "Neutral white";
  return "Cool white";
}

// Task/accent layer appropriate to the room. Ambient alone (a flat wash of
// ceiling cans) is the most common lighting mistake; every room gets at
// least one deliberate second layer.
function roomLayers(input: LightingInput, perFixture: number): LightLayer[] {
  const ambient: LightLayer = {
    kind: "ambient",
    label: "Ambient",
    detail: `recessed on a dimmer, ~${perFixture} lm each`,
  };
  const extra: Partial<Record<RoomType, LightLayer>> = {
    kitchen: { kind: "task", label: "Task", detail: "under-cabinet strip over counters, ~300 lm/ft" },
    bath: { kind: "task", label: "Task", detail: "vanity sconces flanking the mirror at eye level" },
    dining: { kind: "feature", label: "Feature", detail: "pendant over the table, hung 30-36 in above the top" },
    living: { kind: "accent", label: "Accent", detail: "table and floor lamps for evening layers" },
    bedroom: { kind: "task", label: "Task", detail: "bedside reading at ~2700K, switched separately" },
    laundry: { kind: "task", label: "Task", detail: "task light over the folding and sink area" },
    office: { kind: "task", label: "Task", detail: "adjustable desk task light, ~500 lm, glare controlled" },
  };
  const layers = [ambient];
  const e = extra[input.roomType];
  if (e) layers.push(e);
  return layers;
}

export function planLighting(input: LightingInput): LightingPlan {
  const profile = ROOM_LIGHTING[input.roomType] ?? ROOM_LIGHTING.other;
  const { cct, cctBand, cu } = profile;

  // Dim daylight raises the electric target a touch; bright daylight trims it.
  const daylightAdj =
    input.daylight === "low" ? 1.1 : input.daylight === "high" ? 0.9 : 1.0;
  const recommendedTargetFc = Math.round(profile.fc * daylightAdj);
  const targetFc = input.targetFcOverride ?? recommendedTargetFc;

  // Lumen method: lumens = fc x area / (CU x LLF). Brightness feeds it, so an
  // override recomputes the whole ambient target.
  const ambientLumens = Math.round((targetFc * input.sqft) / (cu * LLF));

  // Real dimensions when given; otherwise estimate from area and an assumed
  // aspect ratio.
  const width = input.roomWidthFt ?? Math.sqrt(input.sqft / AVG_ROOM_RATIO);
  const length = input.roomLengthFt ?? input.sqft / width;
  const exactDims = input.roomLengthFt !== undefined && input.roomWidthFt !== undefined;

  // Recommended layout: spacing ~= ceiling / 2 (clamped), which sets the grid.
  const recSpacing = Math.max(3, Math.min(6, +(input.ceilingHeightFt / 2).toFixed(1)));
  const recCols = Math.max(1, Math.round(length / recSpacing));
  const recRows = Math.max(1, Math.round(width / recSpacing));
  const recommendedFixtureCount = recCols * recRows;

  // A user-chosen fixture count re-lays-out the grid and its spacing.
  const overrode = input.fixtureCountOverride !== undefined;
  const { cols, rows } = overrode
    ? gridForCount(input.fixtureCountOverride as number, length, width)
    : { cols: recCols, rows: recRows };
  const fixtureCount = cols * rows;
  const spacingFt = overrode ? +(length / cols).toFixed(1) : recSpacing;
  const wallOffsetFt = +(spacingFt / 2).toFixed(1);
  const lumensPerFixture = Math.round(ambientLumens / fixtureCount);

  const layers = roomLayers(input, lumensPerFixture);

  const reasoning: string[] = [];
  reasoning.push(
    `${cct}K recommended for a ${input.roomType}: ${
      cct <= 2700
        ? "warm light relaxes and flatters skin in a rest and gather space"
        : cct < 3300
        ? "soft white stays cozy while keeping surfaces true"
        : cct < 4000
        ? "neutral white keeps food, faces and finishes accurate for task work"
        : "cool white maximizes contrast and alertness for detailed work"
    }.`
  );
  if (input.orientation === "north") {
    reasoning.push(
      `North light runs cool, so a warmer bulb offsets it. Effective color is pigment times light, so paint is later judged under ${cct}K, not in the abstract.`
    );
  }
  reasoning.push(
    `Lumen method: ${targetFc} fc x ${input.sqft} sqft / (CU ${cu} x LLF ${LLF}) = ${ambientLumens.toLocaleString()} lm of ambient.${
      input.targetFcOverride !== undefined ? ` You set ${targetFc} fc (recommended ${recommendedTargetFc}).` : ""
    }`
  );
  reasoning.push(
    overrode
      ? `You set ${fixtureCount} fixtures, laid out ${cols} x ${rows} at ~${spacingFt} ft on center (recommended ${recommendedFixtureCount}).`
      : `Spacing follows the ceiling: ${input.ceilingHeightFt} ft / 2 = ${spacingFt} ft between cans, ${wallOffsetFt} ft off the walls, giving a ${cols} x ${rows} grid${exactDims ? ` across the ${+length.toFixed(1)} x ${+width.toFixed(1)} ft room` : ""}.`
  );
  reasoning.push(
    `Put the ambient layer on a dimmer and add the task or accent layer above. A single flat wash of ceiling cans is the most common lighting mistake.`
  );
  if (input.roomType === "dining") {
    reasoning.push(
      `Dining leans dramatic: dim the ambient and let the pendant carry the table, sized to about half the table width.`
    );
  }

  return {
    cct,
    cctBand,
    cctLabel: cctLabel(cct),
    targetFc,
    ambientLumens,
    spacingFt,
    wallOffsetFt,
    cols,
    rows,
    fixtureCount,
    lumensPerFixture,
    recommendedTargetFc,
    recommendedFixtureCount,
    layers,
    reasoning,
  };
}

// Derive a daylight level from a RoomContext-style window ratio, so the
// engine can plan from an existing room's light profile.
export function daylightFromWindowRatio(windowRatio: number): DaylightLevel {
  if (windowRatio < 0.1) return "low";
  if (windowRatio < 0.18) return "medium";
  return "high";
}
